/**
 * module-automations.ts — współdzielone typy i enumy automatyzacji modułów (F12.D2).
 *
 * Port z ITE Workflow365: reguła = (wiele) trigger(ów) → warunki → (wiele) akcji, per moduł.
 * Jedno źródło prawdy dla walidacji API (`apps/api`) i buildera UI (`apps/admin`).
 *
 * Faza A: moduły custom, triggery zdarzeniowe, rdzeń akcji. Kolejne triggery/akcje
 * (czasowe, create_record, start_approval, procedury, tagi, archiwizacja) dochodzą w fazach B–D.
 */

/** Zdarzenie cyklu życia rekordu, które napędza triggery event-driven. */
export const AUTOMATION_EVENTS = ['create', 'update', 'delete', 'scheduled'] as const;
export type AutomationEvent = (typeof AUTOMATION_EVENTS)[number];

/**
 * Typy wyzwalaczy silnika.
 * - Event-driven (Faza A): odpalane fire-and-forget po zapisie rekordu.
 * - Time-based (Faza C): odpalane przez cron (`cron:module-automations`), zdarzenie `scheduled`.
 *
 * Kształty `trigger_config` dla triggerów czasowych:
 * - `stale_entity`         { days?: number; hours?: number } — rekord bez zmiany (updated_at)
 *                          od co najmniej podanego czasu. Domyślnie 7 dni gdy brak configu.
 * - `due_date_approaching` { field: string; days?: number } — pole daty mieści się w oknie
 *                          [teraz, teraz + days]. Domyślnie 3 dni.
 * - `due_date_passed`      { field: string; grace_days?: number } — pole daty < teraz - grace.
 *                          Domyślnie grace 0.
 * - `time_based`           { schedule?: 'hourly'|'daily'|'weekly'|'monthly' } — cyklicznie na
 *                          wszystkich aktywnych rekordach spełniających warunki. Kadencję (jak
 *                          często) wyznacza `schedule` (domyślnie 'daily'), a łączny limit
 *                          wykonań na rekord — `execution_limit`.
 */
export const AUTOMATION_TRIGGER_TYPES = [
  'entity_created',
  'entity_updated',
  'entity_deleted',
  'field_updated',
  'field_equals',
  'status_changed',
  // Faza C — czasowe (cron):
  'stale_entity',
  'due_date_approaching',
  'due_date_passed',
  'time_based',
] as const;
export type AutomationTriggerType = (typeof AUTOMATION_TRIGGER_TYPES)[number];

/** Podzbiór wyzwalaczy czasowych — skanowanych przez cron, nie przez zapis rekordu. */
export const AUTOMATION_TIME_TRIGGER_TYPES = [
  'stale_entity',
  'due_date_approaching',
  'due_date_passed',
  'time_based',
] as const satisfies readonly AutomationTriggerType[];
export type AutomationTimeTriggerType = (typeof AUTOMATION_TIME_TRIGGER_TYPES)[number];

/** Czy dany typ wyzwalacza jest czasowy (skanowany przez cron). */
export function isTimeTrigger(type: string): type is AutomationTimeTriggerType {
  return (AUTOMATION_TIME_TRIGGER_TYPES as readonly string[]).includes(type);
}

/** Harmonogramy dla wyzwalacza `time_based`. */
export const AUTOMATION_SCHEDULES = ['hourly', 'daily', 'weekly', 'monthly'] as const;
export type AutomationSchedule = (typeof AUTOMATION_SCHEDULES)[number];

/**
 * Typy akcji silnika.
 * Faza A: update_field, assign_owner, send_email, send_notification, webhook.
 * Faza D: create_record, start_approval, add_tag, remove_tag, archive, unarchive.
 *
 * Kształty `config` dla akcji Fazy D:
 * - `create_record`  { target_module_slug: string; field_values?: Record<string,unknown> }
 *                    — tworzy rekord w innym module Kreatora; wartości interpolowane {record.*}.
 * - `start_approval` { process_id?: string } — uruchamia obieg akceptacji na bieżącym rekordzie
 *                    (moduł Kreatora → target `custom.<slug>`). Brak process_id → domyślny proces
 *                    modułu (custom_modules.default_process_id). Miękki fail (nie blokuje).
 * - `add_tag`/`remove_tag` { field: string; tag: string } — dopisuje/usuwa wartość w kolumnie
 *                    tablicowej TEXT[] rekordu (np. `tags` kontrahenta lub pole modułu typu tags).
 * - `archive`/`unarchive` { } — ustawia is_archived (moduły Kreatora; systemowe nie mają kolumny).
 *
 * `call_procedure`/`call_function` — świadomie POMINIĘTE: repo nie ma generycznego rejestru
 * procedur wołanych z automatyzacji (tylko zamknięta whitelista hooków tenantowych).
 */
export const AUTOMATION_ACTION_TYPES = [
  'update_field',
  'assign_owner',
  'send_email',
  'send_notification',
  'webhook',
  // Faza D:
  'create_record',
  'start_approval',
  'add_tag',
  'remove_tag',
  'archive',
  'unarchive',
] as const;
export type AutomationActionType = (typeof AUTOMATION_ACTION_TYPES)[number];

/** Limit wykonań per (automatyzacja, rekord). */
export const AUTOMATION_EXECUTION_LIMITS = ['once', 'daily', 'weekly', 'always'] as const;
export type AutomationExecutionLimit = (typeof AUTOMATION_EXECUTION_LIMITS)[number];

export const AUTOMATION_TRIGGER_LOGIC = ['AND', 'OR'] as const;
export type AutomationTriggerLogic = (typeof AUTOMATION_TRIGGER_LOGIC)[number];

/** Operatory warunków (kompatybilne z lib/processes/conditions.ts). */
export const AUTOMATION_CONDITION_OPERATORS = [
  '==',
  '!=',
  '>',
  '<',
  '>=',
  '<=',
  'in',
  'contains',
  'is_null',
  'is_not_null',
] as const;
export type AutomationConditionOperator = (typeof AUTOMATION_CONDITION_OPERATORS)[number];

/** Pojedynczy warunek albo grupa (rekurencyjnie, jak ConditionConfig w Procesach). */
export interface AutomationCondition {
  field?: string;
  operator?: AutomationConditionOperator;
  value?: unknown;
  logic?: AutomationTriggerLogic;
  children?: AutomationCondition[];
}

export interface AutomationTrigger {
  trigger_type: AutomationTriggerType;
  trigger_config?: Record<string, unknown>;
}

export interface AutomationAction {
  type: AutomationActionType;
  config?: Record<string, unknown>;
}

/** Kształt rekordu automatyzacji zwracany przez API (camelCase). */
export interface ModuleAutomationDTO {
  id: string;
  moduleId: string | null;
  systemModuleSlug: string | null;
  name: string;
  description: string | null;
  isActive: boolean;
  triggers: AutomationTrigger[];
  triggerLogic: AutomationTriggerLogic;
  conditionGroups: AutomationCondition[];
  actions: AutomationAction[];
  executionLimit: AutomationExecutionLimit;
  priority: number;
  executionCount: number;
  successCount: number;
  failedCount: number;
  lastExecutedAt: string | null;
  lastError: string | null;
}

/** Wpis logu wykonania (do UI historii — Faza D; DTO gotowe już teraz). */
export interface ModuleAutomationLogDTO {
  id: string;
  automationId: string;
  entityId: string | null;
  event: string | null;
  triggerType: string | null;
  status: 'success' | 'failed' | 'skipped';
  actionsExecuted: Array<{ type: string; success: boolean; detail?: string; error?: string }>;
  errorMessage: string | null;
  executionTimeMs: number | null;
  executedAt: string;
}
